/**
 * Punto de entrada del renderer: resuelve la metadata y elige la vista.
 *
 * Es el componente que montan las rutas `/m/:modelName` y
 * `/m/:modelName/:recordId`. Todo lo demás cuelga de acá.
 *
 * Cómo se decide el modo:
 *
 * - Con `recordId` en la ruta, siempre formulario. Ver un registro es abrirlo.
 * - Sin `recordId`, el modo sale de `?view=`; si no viene, se usa el primero de
 *   los declarados por la acción del menú y, en su defecto, por el modelo.
 *
 * Los modos disponibles se derivan de las vistas que el modelo declara. Un
 * modelo sin `views.kanban` no ofrece kanban, y eso hace que el alternador
 * aparezca solo donde tiene sentido, sin configurar nada en el frontend.
 *
 * El modo viaja en la URL a propósito: así se puede compartir el enlace de un
 * tablero, y volver atrás en el navegador devuelve la vista que uno tenía.
 *
 * **El menú también viaja en la URL** (`?menu=`), y de ahí sale el `baseDomain`.
 * Un menú es "modelo más filtro": «Clientes» es `base.partner` con
 * `[("is_customer","=",true)]`. Sin este paso, entrar por «Clientes» mostraría
 * los 18 contactos y las cuatro entradas de Contactos serían la misma pantalla.
 */

import clsx from 'clsx';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { DOMAIN_PARAM, findMenuByName, MENU_PARAM, parseDomainParam } from '@/components/shell/menu-utils';
import { ErrorState, LoadingState } from '@/components/shell/primitives';
import { useMenusQuery, useMetaDeFicha } from '@/components/shell/queries';
import type { Domain, ModelSpec, ViewMode } from '@/lib/types';
import { CalendarView } from '@/views/CalendarView';
import { FormView } from '@/views/FormView';
import { KanbanView } from '@/views/KanbanView';
import { ListView } from '@/views/ListView';
import { precargarFicha } from '@/views/precargarFicha';
import { useCompanyId } from '@/views/queries';
import { ReportScreen } from '@/views/ReportScreen';

/** Modos de colección, en el orden en que se ofrecen. El formulario no entra: no es alternativa. */
const MODOS_COLECCION: ViewMode[] = ['list', 'kanban'];

const ETIQUETA_MODO: Record<string, string> = {
  list: 'Lista',
  kanban: 'Tablero',
};

export interface ViewSwitcherProps {
  /** Modelo a dibujar. Por defecto, el de la ruta. */
  modelName?: string;
  /** Registro abierto. Por defecto, el de la ruta. */
  recordId?: number | 'new' | null;
  /** Dominio base de la acción que trajo acá. Si no se pasa, sale del menú de la URL. */
  baseDomain?: Domain;
  /** Modos permitidos por la acción. Si no se pasa, se deducen del menú y de la metadata. */
  viewModes?: ViewMode[];
}

function modosDisponibles(spec: ModelSpec, declarados?: ViewMode[]): ViewMode[] {
  const desdeMetadata = MODOS_COLECCION.filter((modo) => spec.views[modo] !== undefined);
  const base = desdeMetadata.length > 0 ? desdeMetadata : (['list'] as ViewMode[]);
  if (declarados === undefined) return base;
  const permitidos = declarados.filter((modo) => base.includes(modo));
  return permitidos.length > 0 ? permitidos : base;
}

export function ViewSwitcher({ modelName, recordId, baseDomain, viewModes }: ViewSwitcherProps) {
  const params = useParams<{ modelName: string; recordId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const modelo = modelName ?? params.modelName ?? null;
  const registroCrudo = recordId !== undefined ? recordId : parametroRegistro(params.recordId);
  const nombreMenu = searchParams.get(MENU_PARAM);

  // Sobre un registro concreto la metadata se pide con `?id=`: así el `enabled`
  // de las acciones de servidor sale evaluado contra ESE registro (reglas de
  // fila + dominio de disponibilidad) y no solo contra los permisos del modelo.
  // Sin esto, el formulario de un período ya cerrado ofrecía «Liquidar»
  // habilitado y el usuario se enteraba con un 400.
  // La metadata del modelo (cacheada largo) más la botonera de ESTE registro
  // (liviana), en paralelo. Ver `useMetaDeFicha`.
  const meta = useMetaDeFicha(modelo, typeof registroCrudo === 'number' ? registroCrudo : null);
  const menus = useMenusQuery();

  // LA FICHA EN PARALELO CON LA METADATA. Al abrir un registro esta pantalla
  // esperaba la metadata con `?id=` (un pedido entero) y recién entonces
  // montaba el formulario, que recién entonces pedía los datos: dos pedidos en
  // fila, medio segundo fijo cada uno más sus consultas. Los campos que se
  // piden salen de la metadata del MODELO —la sin id, que cambia sólo con un
  // deploy y se cachea diez minutos—: si está en la caché (se viene de la
  // lista, que es el caso normal), la ficha se pide ya, con la misma clave que
  // va a usar el formulario. Si no está, se sigue como antes.
  const cliente = useQueryClient();
  const companyId = useCompanyId();
  useEffect(() => {
    if (modelo === null || typeof registroCrudo !== 'number') return;
    // Datos, botonera, seguimiento, adjuntos y grillas visibles, todos en la
    // misma tanda. Ver `precargarFicha`.
    precargarFicha(cliente, companyId, modelo, registroCrudo, { completo: true });
  }, [cliente, companyId, modelo, registroCrudo]);

  // El nodo del menú por el que se entró. Mientras el árbol carga es `null` y la
  // pantalla se dibuja sin filtro base: es un instante, y la alternativa —no
  // dibujar nada— haría parpadear toda la lista en cada navegación.
  const nodo = useMemo(() => {
    if (nombreMenu === null || nombreMenu === '' || menus.data === undefined) return null;
    const encontrado = findMenuByName(menus.data, nombreMenu);
    // Un menú de otro modelo no puede aportar su filtro: sus campos no existen acá.
    if (encontrado === null || (modelo !== null && encontrado.model !== modelo)) return null;
    return encontrado;
  }, [menus.data, nombreMenu, modelo]);

  // Dominio base cuando se entra por un botón inteligente: viaja en `?domain=` ya
  // resuelto (con el `$id` del registro origen sustituido por el backend). No hay
  // menú detrás, así que es la única fuente del filtro en ese caso.
  const dominioUrl = useMemo(() => parseDomainParam(searchParams.get(DOMAIN_PARAM)), [searchParams]);

  // El dominio base se aplica SIEMPRE y no se puede quitar desde la UI: las vistas
  // lo reciben aparte de los filtros del usuario y los combinan con AND. Precedencia:
  // el prop explícito, después el filtro del menú, y por último el de la URL.
  const dominio = useMemo(
    () => baseDomain ?? nodo?.domain ?? dominioUrl ?? [],
    [baseDomain, nodo, dominioUrl],
  );
  const modosDeclarados = viewModes ?? nodo?.view_modes;

  if (modelo === null) {
    return <ErrorState message="La dirección no indica ningún modelo." />;
  }

  if (meta.isPending) return <LoadingState label="Preparando la pantalla…" />;

  // Con `?menu=` se espera al árbol antes de dibujar: pintar la lista sin el
  // filtro del menú mostraría por un instante los registros que ese menú
  // justamente excluye — los 18 contactos bajo el título «Clientes».
  if (nombreMenu !== null && nombreMenu !== '' && menus.isPending) {
    return <LoadingState label="Preparando la pantalla…" />;
  }

  if (meta.isError) {
    const status = (meta.error as { status?: number }).status;
    /*
      El mensaje del servidor se muestra tal cual SALVO en 403 y 404, que son
      los dos casos en los que nombra el modelo: «No tenés permiso de lectura
      sobre 'accounting.journal_entry'». Ese texto está bien para un log y muy
      mal para la persona que se topó con la pantalla: es el nombre de la tabla,
      en inglés y con punto, y no le dice qué hacer. Acá se reemplaza por una
      frase en castellano que además indica el camino de salida. Se puede porque
      no hay nada que interpretar: 403 es siempre lo mismo y 404 también.

      El resto de los errores —red, 500— siguen mostrando lo que dijo el
      servidor, que ahí sí es la única pista útil, y conservan «Reintentar».
    */
    const mensaje =
      status === 403
        ? 'Tu usuario no tiene permiso para ver esta información. Si la necesitás para trabajar, pedísela a quien administra el sistema.'
        : status === 404
          ? 'Esta pantalla no existe, o el módulo que la trae no está instalado en esta empresa.'
          : meta.error.message;
    return (
      <ErrorState
        title={status === 403 ? 'No tenés acceso a esta pantalla' : 'No se pudo abrir esta pantalla'}
        message={mensaje}
        onRetry={
          status === 403 || status === 404
            ? undefined
            : () => {
                void meta.refetch();
              }
        }
      />
    );
  }

  const spec = meta.data;

  /*
    Modo `report`: la acción del menú no abre una lista sino una PANTALLA DE
    REPORTE —parámetros arriba, cuadro abajo, PDF al costado—. Es lo que pone
    los estados contables y los libros de IVA en el menú en vez de esconderlos
    en el formulario de un registro que hay que encontrar primero.

    Sigue siendo genérico: lo único que el módulo declara es el nombre de la
    acción de servidor que hace la cuenta (`context.report_action`). Sin ese
    dato el modo no aplica y la pantalla cae a la lista de siempre, que es lo
    que corresponde: una declaración incompleta no debe dejar una pantalla en
    blanco.
  */
  const accionDeReporte = nodo?.context?.report_action;
  if (
    registroCrudo === null &&
    (modosDeclarados ?? []).includes('report') &&
    typeof accionDeReporte === 'string' &&
    accionDeReporte !== ''
  ) {
    return (
      <ReportScreen
        key={`${spec.model}-${accionDeReporte}`}
        spec={spec}
        actionName={accionDeReporte}
        title={nodo?.label ?? spec.label}
      />
    );
  }

  /*
    Modo `calendar`: la acción del menú no abre una lista sino un MES A LA VISTA,
    con lo que vence cada día. Es el pedido del comprador («vencimientos de las
    cosas que vendemos») y sigue, línea por línea, el mismo mecanismo que el modo
    `report` de acá arriba: lo único que el módulo declara es sobre QUÉ CAMPO DE
    FECHA se arma el mes (`context.calendar_field`). Sin ese dato el modo no
    aplica y la pantalla cae a la lista de siempre — una declaración incompleta
    no debe dejar una pantalla en blanco.

    La vista es genérica: no sabe qué es una venta ni qué es un vencimiento.
    Recibe el spec, el nombre del campo y dibuja. Ver `views/CalendarView.tsx`.
  */
  const campoDelCalendario = nodo?.context?.calendar_field;
  if (
    registroCrudo === null &&
    (modosDeclarados ?? []).includes('calendar') &&
    typeof campoDelCalendario === 'string' &&
    campoDelCalendario !== ''
  ) {
    const hermanoMenu = nodo?.context?.calendar_sibling_menu;
    const hermanoLabel = nodo?.context?.calendar_sibling_label;
    /*
      La agenda unificada. Si el módulo declara la ruta del endpoint que junta
      TODO lo que vence —las ventas, los reclamos con plazo y los pendientes
      agendados—, el calendario se arma con eso; si no, con el modelo del spec y
      su `calendar_field`, que es lo que hacía antes.

      Va como un dato más del contexto y no como un modo de vista nuevo a
      propósito: es la MISMA pantalla mostrando más cosas, no otra pantalla. Y
      declarativo, para que un despliegue sin el módulo dueño del endpoint caiga
      solo al calendario de una fuente en vez de pedir una URL que no existe.
    */
    const rutaDeLaAgenda = nodo?.context?.calendar_agenda_url;
    return (
      <CalendarView
        key={`${spec.model}-${campoDelCalendario}`}
        spec={spec}
        dateField={campoDelCalendario}
        title={nodo?.label ?? spec.label_plural}
        baseDomain={dominio}
        menuName={nombreMenu}
        {...(typeof rutaDeLaAgenda === 'string' && rutaDeLaAgenda !== ''
          ? { agendaUrl: rutaDeLaAgenda }
          : {})}
        {...(typeof hermanoMenu === 'string' ? { siblingMenu: hermanoMenu } : {})}
        {...(typeof hermanoLabel === 'string' ? { siblingLabel: hermanoLabel } : {})}
      />
    );
  }

  if (registroCrudo !== null) {
    return (
      <FormView
        key={`${spec.model}-${String(registroCrudo)}`}
        spec={spec}
        recordId={registroCrudo}
        menuName={nombreMenu}
        {...(nodo?.context === undefined ? {} : { context: nodo.context })}
      />
    );
  }

  const modos = modosDisponibles(spec, modosDeclarados);
  const pedido = searchParams.get('view') as ViewMode | null;
  const modo: ViewMode = pedido !== null && modos.includes(pedido) ? pedido : (modos[0] ?? 'list');

  const alternador =
    modos.length < 2 ? null : (
      <div role="group" aria-label="Modo de vista" className="inline-flex rounded-md border border-line p-0.5">
        {modos.map((candidato) => (
          <button
            key={candidato}
            type="button"
            aria-pressed={modo === candidato}
            onClick={() => {
              setSearchParams(
                (actual) => {
                  const copia = new URLSearchParams(actual);
                  copia.set('view', candidato);
                  return copia;
                },
                { replace: true },
              );
            }}
            className={clsx(
              'rounded px-3 py-1.5 text-sm font-medium transition-colors',
              modo === candidato ? 'bg-accent text-accent-contrast' : 'text-fg-muted hover:text-fg',
            )}
          >
            {ETIQUETA_MODO[candidato] ?? candidato}
          </button>
        ))}
      </div>
    );

  // La clave incluye el menú: pasar de «Clientes» a «Proveedores» es la misma
  // ruta con otro filtro, y sin remontar quedarían la paginación y la selección
  // de la pantalla anterior sobre registros que ya no son los mismos.
  const clave = `${spec.model}-${nombreMenu ?? ''}`;

  if (modo === 'kanban') {
    return (
      <KanbanView
        key={`${clave}-kanban`}
        spec={spec}
        baseDomain={dominio}
        menuName={nombreMenu}
        modeSwitcher={alternador}
      />
    );
  }

  return (
    <ListView
      key={`${clave}-list`}
      spec={spec}
      baseDomain={dominio}
      menuName={nombreMenu}
      modeSwitcher={alternador}
    />
  );
}

/** `"new"` o un id numérico. Cualquier otra cosa no es un registro. */
function parametroRegistro(crudo: string | undefined): number | 'new' | null {
  if (crudo === undefined || crudo === '') return null;
  if (crudo === 'new') return 'new';
  const numero = Number(crudo);
  return Number.isInteger(numero) && numero > 0 ? numero : null;
}
